import PasswordField from "@/components/feature/auth/PasswordField";
import { AuthExperience } from "@/components/feature/mobile/Experience";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getDataClient } from "@/lib/dataClient";
import { roleHomePath, translateAuthError } from "@/hooks/useAuth";
import { hasVerifiedTotp } from "@/hooks/useMfa";
import MfaCodeForm from "./MfaCodeForm";
import Logo from "@/components/feature/Logo";

type RoleKey = "ogretmen" | "admin";

const configs = {
 ogretmen: {wrongRoleMessage:"Bu hesap bir öğretmen hesabı değil. Lütfen size uygun giriş sayfasını kullanın.", siblingLabel:"Yönetici girişi", siblingPath:"/yonetici/giris"},
 admin: {wrongRoleMessage:"Bu hesap bir yönetici hesabı değil. Lütfen size uygun giriş sayfasını kullanın.", siblingLabel:"Öğretmen girişi", siblingPath:"/ogretmen/giris"}
};

export default function RoleLogin({ role }: { role: RoleKey }) {
  const cfg = configs[role];
  const navigate = useNavigate();
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState<{ factorId: string; role: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus(null);

    const form = e.currentTarget;
    const fd = new FormData(form);
    const email = String(fd.get("email") || "").trim();
    const password = String(fd.get("password") || "");

    if (!email || !password) return;

    setLoading(true);
    try {
      const dataClient = getDataClient();
      const { data, error } = await dataClient.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setStatus(translateAuthError(error.message));
        return;
      }

      const actualRole = (data.user?.user_metadata?.role as string) || "ogrenci";
      if (actualRole !== role) {
        setStatus(cfg.wrongRoleMessage);
        return;
      }

      const { has, factorId } = await hasVerifiedTotp();
      if (has) {
        setMfa({ factorId, role: actualRole });
        return;
      }

      navigate(roleHomePath(actualRole as never), { replace: true });
    } catch {
      setStatus("Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  return (<AuthExperience role={role}>
            {mfa ? (
              <MfaCodeForm
                factorId={mfa.factorId}
                onSuccess={() => navigate(roleHomePath(mfa.role as never), { replace: true })}
                onBack={() => setMfa(null)}
              />
            ) : (
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label
                  htmlFor={`${role}-email`}
                  className="block text-sm font-medium text-foreground-800"
                >
                  E-posta
                </label>
                <input
                  id={`${role}-email`}
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="ornek@mail.com"
                  className="mt-1.5 w-full h-11 px-3.5 rounded-md border border-background-200 bg-background-100/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label
                  htmlFor={`${role}-password`}
                  className="block text-sm font-medium text-foreground-800"
                >
                  Şifre
                </label>
                <PasswordField
                  id={`${role}-password`}
                  name="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="mt-1.5 w-full h-11 px-3.5 rounded-md border border-background-200 bg-background-100/60 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none transition-colors"
                />
              </div>

              <label className="block text-sm">Doğrulama kodu (etkinleştirdiyseniz)<input name="otp" autoComplete="one-time-code" maxLength={20} className="mt-2 w-full rounded-lg border p-3" placeholder="6 haneli kod veya yedek kod" /></label>
<button
                type="submit"
                disabled={loading}
                className="w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
              </button>
            </form>
            )}

            {status && (
              <p className="mt-4 text-sm text-foreground-600 bg-background-100 rounded-md p-3 leading-relaxed">
                {status}
              </p>
            )}

            <div className="mt-6 flex flex-col gap-2 text-sm text-center text-foreground-600">
              <Link to="/sifremi-unuttum" className="text-primary-700 font-semibold">Şifremi unuttum</Link>
              <p>
                Öğrenci veya veli misiniz?{" "}
                <Link to="/giris" className="text-primary-700 font-semibold hover:text-primary-800">
                  Normal girişe gidin
                </Link>
              </p>
              <p>
                <Link
                  to={cfg.siblingPath}
                  className="text-primary-700 font-semibold hover:text-primary-800"
                >
                  {cfg.siblingLabel}
                </Link>
              </p>
            </div>
</AuthExperience>);
}

